import axios from "axios";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useUserAuth } from "../context/UserAuthContext";
import { FiMapPin } from "react-icons/fi";
import { workingAddressSchema } from "./schemas";
import CustomSelect from "./customSelect/CustomSelect";

const WorkingAddress = ({ onSuccess }) => {
  const { user, token, language } = useUserAuth();
  const merchant_id = user?.uid;
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

  // State for dropdown data
  const [country, setCountry] = useState([]);
  const [state, setState] = useState([]);
  const [city, setCity] = useState([]);
  const [locality, setLocality] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get(`${base_url}/api/v1/countries`);
        setCountry(res.data.data);
      } catch (error) {
        console.log(`Error fetching countries: ${error}`);
        toast.error("Failed to load countries");
      }
    };
    fetchCountries();
  }, [base_url]);

  // Fetch states based on selected country
  const fetchStates = async (country_id) => {
    try {
      const res = await axios.get(
        `${base_url}/api/v1/states/country/${country_id}`,
      );
      setState(
        res.data.data.map((doc) => ({
          key: doc.id,
          value: doc.name,
          country_id: doc.country_id,
        })),
      );
    } catch (error) {
      console.log(`Error fetching states: ${error}`);
      toast.error("Failed to load states");
    }
  };

  // Fetch cities based on selected state
  const fetchCities = async (state_id) => {
    try {
      setCity([]);
      const res = await axios.get(
        `${base_url}/api/v1/cities/state/${state_id}`,
      );
      setCity(
        res.data.data.map((doc) => ({
          key: doc.id,
          value: doc.name,
          state_id: doc.state_id,
        })),
      );
    } catch (error) {
      console.log(`Error fetching cities: ${error}`);
      toast.error("Failed to load cities");
    }
  };

  // Fetch localities based on selected city
  const fetchLocalities = async (city_id) => {
    try {
      setLocality([]);
      const res = await axios.get(
        `${base_url}/api/v1/localities/city/${city_id}`,
      );
      setLocality(
        res.data.data.map((doc) => ({
          key: doc.id,
          value: doc.name,
          city_id: doc.city_id,
        })),
      );
    } catch (error) {
      console.log(`Error fetching localities: ${error}`);
      toast.error("Failed to load localities");
    }
  };

  const formik = useFormik({
    initialValues: {
      country_id: "",
      state_id: "",
      city_id: "",
      pincode: "",
      locality_id: "",
      landmark: "",
      full_address: "",
    },
    validationSchema: workingAddressSchema,
    onSubmit: async (values) => {
      const {
        country_id,
        state_id,
        city_id,
        pincode,
        locality_id,
        landmark,
        full_address,
      } = values;

      try {
        setLoading(true);
        const data = {
          country_id,
          state_id,
          city_id,
          pincode,
          locality_id: locality_id || null,
          landmark: landmark || null,
          full_address: full_address || null,
        };

        const response = await axios.post(
          `${base_url}/api/v1/users/working-address/${merchant_id}`,
          data,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.status === 200 || response.status === 201) {
          toast.success("Working address added successfully");
          formik.resetForm();
          if (onSuccess) onSuccess(response.data.data);
        } else {
          toast.error(response.data.message || "Something went wrong");
        }
      } catch (err) {
        console.error(err);
        const errorMsg =
          err.response?.data?.message || "Failed to add working address";
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
  });

  // Fetch states when country changes
  useEffect(() => {
    if (formik.values.country_id) {
      fetchStates(formik.values.country_id);
    } else {
      setState([]);
    }
  }, [formik.values.country_id]);

  // Fetch cities when state changes
  useEffect(() => {
    if (formik.values.state_id) {
      fetchCities(formik.values.state_id);
    } else {
      setCity([]);
    }
  }, [formik.values.state_id]);
  // Fetch localities when city changes
  useEffect(() => {
    if (formik.values.city_id) {
      fetchLocalities(formik.values.city_id);
    } else {
      setLocality([]);
    }
  }, [formik.values.city_id]);

  return (
    <div className="page-wrapper">
      <div className="profile-container">
        <div className="profile-card">
          <div className="card">
            <div
              className="card-header text-center text-white"
              style={{ backgroundColor: "#085f9a" }}
            >
              <h5>
                <FiMapPin className="section-header-icon" />
                {language === "en"
                  ? "Working Business Address"
                  : "कार्यस्थल का पता"}
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={formik.handleSubmit} className="row">
                <div className="form-group col-md-6">
                  <label className="form-label">Country</label>
                  <select
                    name="country_id"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.country_id}
                    className={`form-control ${
                      formik.touched.country_id && formik.errors.country_id
                        ? "is-invalid"
                        : ""
                    }`}
                  >
                    <option value="">Select Country</option>
                    {country?.map((item) => (
                      <option value={item.id} key={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.country_id && formik.errors.country_id && (
                    <div className="invalid-feedback">
                      {formik.errors.country_id}
                    </div>
                  )}
                </div>
                <div className="form-group col-md-6">
                  <CustomSelect
                    title="State"
                    placeholder="Select State"
                    name="state_id"
                    data={state?.filter(
                      (pre) => pre?.country_id === formik.values?.country_id,
                    )}
                    defaultValue={formik.values.state_id}
                    onChange={(value) =>
                      formik.setFieldValue("state_id", value)
                    }
                    error={formik.touched.state_id && formik.errors.state_id}
                  />
                </div>
                <div className="form-group col-md-6">
                  <CustomSelect
                    title="City"
                    placeholder="Select City"
                    name="city_id"
                    data={city?.filter(
                      (pre) => pre?.state_id === formik.values?.state_id,
                    )}
                    defaultValue={formik.values.city_id}
                    onChange={(value) => formik.setFieldValue("city_id", value)}
                    error={formik.touched.city_id && formik.errors.city_id}
                  />
                </div>
                <div className="form-group col-md-6">
                  <CustomSelect
                    title={
                      formik.values.country_id === "4Dj1nUEkp4vFOh4HI0CL"
                        ? "Locality"
                        : "Locality"
                    }
                    isRequired={
                      formik.values.country_id === "4Dj1nUEkp4vFOh4HI0CL"
                    }
                    placeholder="Select Locality"
                    name="locality_id"
                    data={locality}
                    defaultValue={formik.values.locality_id}
                    onChange={(value) =>
                      formik.setFieldValue("locality_id", value)
                    }
                    error={
                      formik.touched.locality_id && formik.errors.locality_id
                    }
                  />
                </div>
                <div className="form-group col-md-6">
                  <label className="form-label">Landmark</label>
                  <input
                    type="text"
                    name="landmark"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.landmark}
                    className="form-control"
                  />
                </div>
                <div className="form-group col-md-6">
                  <label className="form-label">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.pincode}
                    className={`form-control ${
                      formik.touched.pincode && formik.errors.pincode
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.pincode && formik.errors.pincode && (
                    <div className="invalid-feedback">
                      {formik.errors.pincode}
                    </div>
                  )}
                </div>
                <div className="form-group col-md-12">
                  <label className="form-label">Full Address</label>
                  <textarea
                    name="full_address"
                    rows={3}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.full_address}
                    className="form-control"
                    placeholder="Enter complete address (e.g., building name, floor, area, landmark, city, state, pincode)"
                  />
                </div>
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save Working Address"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkingAddress;
